import { NgIf } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { DocumentSelectOption, HaftpflichtSchadenSpritzschaden, HaftpflichtSchadenWithAufstellung } from '@gsm/entities';
import { DynamicFormControlModel } from '@ng-dynamic-forms/core';
import { CustomDynamicFormService } from '../../custom-dynamic-form.service';
import { getDisplayName } from '../../helpers/display-name.helper';
import { filterEmptyProps } from '../../helpers/filter-props.helper';
import { getGroupValue } from '../../helpers/get-group-value.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { DokumentenAngabeModel } from '../dokumenten-angabe/dokumenten-angabe.model';
import { GeschaedigtePersonDialogData, HaftpflichtGeschaedigtePersonDialogComponent } from '../haftpflicht-geschaedigte-person/haftpflicht-geschaedigte-person-dialog/haftpflicht-geschaedigte-person-dialog.component';
import { HaftpflichtGeschaedigtePersonModel } from '../haftpflicht-geschaedigte-person/haftpflicht-geschaedigte-person.model';
import { AnyFormControlModel } from '../shared-models/any-form-control.model';
import { AufstellungVorhanden, HaftpflichtSchadenVorhanden } from '../shared-models/haftpflichtschaden.model';
import { ZahlungsempfaengerModel } from '../zahlungsempfaenger/zahlungsempfaenger.model';
import { HAFTPFLICHT_WALDARBEITEN_TEMPLATE } from './haftpflicht-sachschaden-waldarbeiten.config';
import { HaftpflichtSachschadenWaldarbeitenModel } from './haftpflicht-sachschaden-waldarbeiten.model';

const DOK_TYP_SPRITZ_SCHADENAUFTELLUNG: DocumentSelectOption = {
  label:                      'Spritzschaden Schadenaufstellung',
  value:                      'SCH_A_SP',
  zahlungsempfaengerRequired: true
};

const DOK_TYP_WALDARBEITEN_SCHADENAUFSTELLUNG: DocumentSelectOption = {
  label:                      'Waldarbeiten Schadenaufstellung',
  value:                      'SCH_A_WA',
  zahlungsempfaengerRequired: true
};

// NOTE:
// The base class CustomDynamicFormControlComponent already includes all inputs and outputs needed, as well
// as additional convenience methods onDisabledChange() and onValueChange, for when model.disabled and model.value change,
// respectively.

@Component({
  selector:    'gsm-haftpflicht-sachschaden-waldarbeiten',
  templateUrl: 'haftpflicht-sachschaden-waldarbeiten.component.html',
  styleUrls:   ['haftpflicht-sachschaden-waldarbeiten.component.scss'],
  standalone:  true,
  imports:     [
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIconButton,
    MatIcon,
    NgIf,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatMiniFabButton
  ]
})
export class HaftpflichtSachschadenWaldarbeitenComponent extends CustomDynamicFormControlComponent<HaftpflichtSachschadenWaldarbeitenModel>
  implements OnInit {

  private dialog = inject(MatDialog);
  private formService = inject(CustomDynamicFormService);

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  protected onModelChange() {
    if (!this.model.value) {
      this.model.value = [];
    }
    this._emitChange();
  }

  private _emitChange() {
    this.group.get(this.model.id)
      .setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _getErrors(): ValidationErrors {
    const isValid = this.model.value?.length;
    return isValid ? null : {geschaedigtePersonen: true};
  }

  getName(schaden: HaftpflichtSchadenWithAufstellung): string {
    return schaden.vorhanden === HaftpflichtSchadenVorhanden.Nachgebracht
      ? 'Wird nachgebracht'
      : schaden.geschaedigtePerson?.nachname
        ? getDisplayName([
          schaden.geschaedigtePerson.vorname,
          schaden.geschaedigtePerson.nachname
        ])
        : 'Unbekannt';
  }

  getAufstellung(schaden: HaftpflichtSchadenWithAufstellung): string {
    switch (schaden.aufstellungVorhanden) {
      case AufstellungVorhanden.Vorhanden:
        return 'Vorhanden';
      case AufstellungVorhanden.Erfassen:
        return 'Schadenaufstellung wurde erstellt';
      case AufstellungVorhanden.Nachgebracht:
        return 'Wird nachgebracht';
      default:
        return '';
    }
  }

  onAddClick() {
    this._editItem({}, null);
  }

  onEditClick(item: HaftpflichtSchadenWithAufstellung, index: number) {
    this._editItem(item, index, true);
  }

  onRemoveClick(index: number) {
    this.model.value = this.model.value.filter((_, i) => i !== index);
    this._emitChange();
  }

  private _editItem(item: HaftpflichtSchadenWithAufstellung, index: number | null, isEditEvent = false) {
    this.dialog
      .open<HaftpflichtGeschaedigtePersonDialogComponent, GeschaedigtePersonDialogData, AnyFormControlModel[]>(
        HaftpflichtGeschaedigtePersonDialogComponent,
        {
          // allow quick closing only in view mode
          disableClose: !this.model.disabled,
          minWidth:     '600px',
          data:         {
            formConfig:      HAFTPFLICHT_WALDARBEITEN_TEMPLATE(item, this.model.value, this.model.additional, isEditEvent),
            heading:
                             this.model.id === 'KERN_HAFTPFLICHT_FF_geschaedigtePersonenWaldarbeiten'
                               ? 'Sachschaden bei Waldarbeiten - Geschädigte Person'
                               : 'Sachschaden Spritzschäden - Geschädigte Person',
            disabled:        this.model.disabled,
            parentComponent: this,
            onFormChange:    this._onFormChange
          }
        }
      )
      .afterClosed()
      .subscribe(result => {
        if (result) {
          const resultEntity = this._getValueFromForm(result);
          this._saveEditedItem(resultEntity, index);
        }
      });
  }

  // Needs to be an arrow function to preserve "this" context.
  private _onFormChange = (models: DynamicFormControlModel[], changeModel?: AnyFormControlModel) => {
    if (changeModel?.id === 'geschaedigtePerson') {
      const geschaedigtePerson = (changeModel as HaftpflichtGeschaedigtePersonModel).value?.geschaedigtePerson;

      // If geschaedigtePerson has a nachname, use it as a Zahlungsempfaenger.
      if (geschaedigtePerson?.nachname) {
        const {
          vorname,
          nachname
        } = geschaedigtePerson;

        // Only add to kunde list if someone with the same name isn't there yet.
        if (!this.model.additional.kundeList?.some(kunde => kunde.vorname === vorname && kunde.nachname === nachname)) {
          const newKundeList = [
            ...(this.model.additional.kundeList || []),
            {
              vorname,
              nachname
            }
          ];

          // Set kunden properties on Zahlungsempfaenger and DokumentenAngabe components.
          const zahlungsempfaenger = this.formService.findModelById<ZahlungsempfaengerModel>('aufstellungZahlungsempfaenger', models);
          const belegeDokumente = this.formService.findModelById<DokumentenAngabeModel>('aufstellungBelege', models);
          zahlungsempfaenger.kunden = newKundeList;
          belegeDokumente.additional.kunden = newKundeList;
        }
      }
    }
  };

  private _saveEditedItem(item: HaftpflichtSchadenWithAufstellung, index: number | null) {
    // index == null means that a new item should be added
    if (index !== null) {
      this.model.value = this.model.value.map((val, i) => (index === i ? item : val));
    } else {
      this.model.value = [
        ...this.model.value,
        item
      ];
    }
    this._emitChange();
  }

  private _getValueFromForm(formModel: AnyFormControlModel[]): HaftpflichtSchadenSpritzschaden {
    const {
      geschaedigtePerson,
      spritzschaden_group: {bioKultur} = {} as any,
      aufstellung_group:   {
                             aufstellungVorhanden,
                             aufstellungFileList,
                             spritzschadenAufstellung,
                             aufstellungZahlungsempfaenger_group: {aufstellungZahlungsempfaenger: zahlungsempfaenger} = {} as any,
                             aufstellungBelege_group:             {aufstellungBelege} = {} as any
                           } = {} as any
    } = getGroupValue(formModel);

    const docTyp = this.model.additional.spritzschaden ? DOK_TYP_SPRITZ_SCHADENAUFTELLUNG : DOK_TYP_WALDARBEITEN_SCHADENAUFSTELLUNG;

    const schadenAufstellung: HaftpflichtSchadenWithAufstellung = aufstellungVorhanden
      ? {
        aufstellungVorhanden,
        belege: aufstellungBelege,
        aufstellung:
                aufstellungVorhanden === AufstellungVorhanden.Vorhanden
                  ? {
                    vorhanden:             'V',
                    typ:                   docTyp,
                    checklistItemFileList: aufstellungFileList,
                    zahlungsempfaenger
                  }
                  : aufstellungVorhanden === AufstellungVorhanden.Nachgebracht
                    ? {
                      vorhanden: 'N',
                      typ:       docTyp
                    }
                    : aufstellungVorhanden === AufstellungVorhanden.Erfassen
                      ? {
                        vorhanden:      'V', // inside the dokument it's considered vorhanden
                        typ:            docTyp,
                        generationData: spritzschadenAufstellung ? {spritzschadenAufstellung} : null,
                        zahlungsempfaenger
                      }
                      : null
      }
      : null;

    return filterEmptyProps<HaftpflichtSchadenSpritzschaden>({
      ...geschaedigtePerson,
      ...schadenAufstellung,
      bioKultur
    });
  }
}
